/**
 * Product detail page with unified UX pattern
 * Features: Skeleton loader, back navigation, premium badge, Help CTA
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useOwnedProducts } from '../hooks/useOwnedProducts';
import { Product } from '../types';
import { getProductImageUrl } from '../utils/imageUtils';
import {
  ArrowLeft,
  ShoppingCart,
  Star,
  Tag,
  Shield,
  Download,
  CreditCard,
  HelpCircle,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import LegalDisclaimer from '../components/common/LegalDisclaimer';

export default function ProductDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { addToCart, items } = useCart();
  const { formatPrice } = useCurrency();
  const { isOwned } = useOwnedProducts();
  const navigate = useNavigate();

  const isProductOwned = id ? isOwned(id) : false;
  const isInCart = items.some((item) => item.productId === id);

  useEffect(() => {
    if (id) {
      loadProduct();
    }
  }, [id]);

  async function loadProduct(): Promise<void> {
    setLoading(true);
    setError('');

    try {
      const data = await productsApi.get(id!);
      setProduct(data);
    } catch (err: any) {
      console.error('Failed to load product:', err);
      setError('Failed to load product. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleAddToCart(): void {
    if (product) {
      addToCart(product);
    }
  }

  function handleGoToCart(): void {
    navigate('/cart');
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back link skeleton */}
          <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-6" />

          <div className="card overflow-hidden">
            {/* Image skeleton */}
            <div className="w-full h-64 md:h-96 bg-gray-200 dark:bg-gray-700 animate-pulse" />

            <div className="p-8">
              {/* Badges skeleton */}
              <div className="flex gap-2 mb-4">
                <div className="h-7 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>

              {/* Title skeleton */}
              <div className="h-10 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />

              {/* Short description skeleton */}
              <div className="h-6 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
              <div className="h-6 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-6" />

              {/* Long description skeleton */}
              <div className="space-y-3 mb-8">
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>

              {/* Price skeleton */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-12 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-md mx-auto">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-6"
          >
            <ArrowLeft size={16} />
            Back to Products
          </Link>

          <div className="card p-8 text-center">
            <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full inline-block mb-4">
              <AlertCircle size={48} className="text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Product Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'The product you\'re looking for doesn\'t exist or has been removed.'}
            </p>
            <button onClick={() => navigate('/products')} className="btn btn-primary">
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isPremium = product.qualityTier === 'premium';

  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Navigation */}
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-6"
        >
          <ArrowLeft size={16} />
          Back to Products
        </Link>

        {/* Product Card */}
        <div className="card overflow-hidden">
          {/* Image with Badges */}
          <div className="relative">
            <img
              src={getProductImageUrl(product.imageUrl)}
              alt={product.title}
              className="w-full h-64 md:h-96 object-cover bg-gray-200 dark:bg-gray-700"
            />
            {/* Owned Badge */}
            {isProductOwned && (
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-green-600 text-white text-sm font-bold rounded-full flex items-center gap-1.5 shadow-lg">
                <CheckCircle size={16} />
                Owned
              </div>
            )}
            {/* Premium Badge */}
            {isPremium && (
              <div className="absolute top-4 right-4 px-3 py-1.5 bg-amber-500 text-white text-sm font-bold rounded-full flex items-center gap-1.5 shadow-lg">
                <Star size={16} fill="currentColor" />
                Premium Guide
              </div>
            )}
          </div>

          <div className="p-8">
            {/* Category & Tier Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400 rounded-full font-medium">
                <Tag size={14} />
                {formatCategory(product.category)}
              </span>
              <span className={`text-sm px-3 py-1.5 rounded-full capitalize font-medium ${
                isPremium
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>
                {product.qualityTier}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {product.title}
            </h1>

            {/* Short Description */}
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
              {product.shortDescription}
            </p>

            {/* Long Description */}
            <div className="prose dark:prose-invert max-w-none mb-8">
              <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {product.longDescription}
              </p>
            </div>

            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Download size={18} className="text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Instant Access</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Download immediately</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <CreditCard size={18} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">One-Time Purchase</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">No subscription</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Shield size={18} className="text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Secure Payment</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SSL encrypted</p>
                </div>
              </div>
            </div>

            {/* Price and CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div>
                {isProductOwned ? (
                  <>
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      Purchased
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      You own this product • Download available
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                      {formatPrice(product.price.amount, product.price.currency)}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      One-time purchase • Lifetime access
                    </p>
                  </>
                )}
              </div>

              <div className="flex gap-3">
                {isProductOwned ? (
                  <Link
                    to="/user?tab=products"
                    className="btn bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 text-lg px-6 py-3"
                  >
                    <Download size={20} />
                    Download
                  </Link>
                ) : isInCart ? (
                  <button
                    onClick={handleGoToCart}
                    className="btn btn-primary inline-flex items-center gap-2 text-lg px-6 py-3"
                  >
                    <CheckCircle size={20} />
                    Go to Cart
                  </button>
                ) : (
                  <button
                    onClick={handleAddToCart}
                    className="btn btn-primary inline-flex items-center gap-2 text-lg px-6 py-3"
                  >
                    <ShoppingCart size={20} />
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <LegalDisclaimer variant="prominent" className="mt-8" />

        {/* Help CTA */}
        <section className="mt-8 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
                <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Have questions about this product?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Check our help center for FAQs and buying guides
                </p>
              </div>
            </div>
            <Link to="/help" className="btn btn-secondary whitespace-nowrap inline-flex items-center gap-2">
              <HelpCircle size={16} />
              Get Help
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

// Helper function to format category names
function formatCategory(category: string): string {
  const names: Record<string, string> = {
    'horse-racing': 'Horse Racing',
    'greyhound-racing': 'Greyhound Racing',
    'lottery': 'Lottery',
    'card-games': 'Card Games',
    'roulette': 'Roulette',
    'multi-game': 'Multi-Game',
  };
  return names[category] || category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
